// Coach Mode V2 helpers. Pure functions only (tested in
// src/tests/coachUtils.test.js and src/tests/coachHomeUtils.test.js).

import { displayTeamName, stripSeasonSuffix } from "../../../utils/teamNames";

// Placeholder for a value the product has no data for (an en dash, never
// an em dash).
export const EMPTY_VALUE = "–";

// Note in place of a 30-day change until 30 days of snapshots exist.
export const NO_30D_HISTORY = "No 30-day history yet";

// bm-positive and bm-attention.
export const STATUS_COLORS = { active: "#1f9d55", pending: "#EDD200" };

// Swing archetypes (Figma "Swing Composition" legend). `dotColor` is the
// chart dot where it differs from the badge dot (Dev. Athlete).
export const ARCHETYPE_META = {
  star: {
    label: "Visibility Star",
    color: "#2196F3",
    blurb: "Low sport • High visibility",
    cacheValueLabel: "Moderate",
    swing: "NILx > CacheValue",
    copy: "Brand asset regardless of on-the-court output. Brands pay for reach, not rebounds.",
  },
  complete: {
    label: "Complete Athlete",
    color: "#FF4400",
    blurb: "High sport • High visibility",
    cacheValueLabel: "Premium",
    swing: "NILx ≈ CacheValue or higher",
    copy: "Strongest market position. NILx amplifies what's already a premium market value.",
  },
  dev: {
    label: "Dev. Athlete",
    color: "#EDD200",
    dotColor: "#FFC107",
    blurb: "Low sport • Low visibility",
    cacheValueLabel: "Emerging",
    swing: "NILx ≈ CacheValue (low)",
    copy: "Both metrics grow together. Trajectory matters more than current position.",
  },
  gem: {
    label: "Hidden Gem",
    color: "#4CAF50",
    blurb: "High sport • Low visibility",
    cacheValueLabel: "Strong",
    swing: "NILx < CacheValue. Invest in visibility to close the gap",
    copy: "NIL market underprices this athlete - upside exists.",
  },
};

export const PLATFORM_LABELS = { instagram: "Instagram", tiktok: "TikTok", x: "Twitter/X", youtube: "YouTube" };

const isMissing = (n) => n === null || n === undefined || Number.isNaN(Number(n));

export const fmtMoney = (n) => (isMissing(n) ? EMPTY_VALUE : `$${Math.round(Number(n)).toLocaleString("en-US")}`);

// 1.29M, 10M, 47.2K, 191K, 950. Trailing zeros go only after a decimal point.
export const fmtCompact = (n) => {
  if (isMissing(n)) return EMPTY_VALUE;
  const v = Number(n);
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${Number((v / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 2))}M`;
  if (abs >= 1_000) return `${Number((v / 1_000).toFixed(abs >= 100_000 ? 0 : 1))}K`;
  return `${Math.round(v)}`;
};

export const fmtMoneyCompact = (n) => (isMissing(n) ? EMPTY_VALUE : `$${fmtCompact(n)}`);

// "+4.2", "−1.5", "+$3.2K": a change with its sign, formatted by `fmt`.
export const fmtSigned = (n, fmt = (v) => `${v}`) => (isMissing(n) ? EMPTY_VALUE : `${Number(n) < 0 ? "−" : "+"}${fmt(Math.abs(Number(n)))}`);

export const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

export const trendLabel = (trend) => {
  if (trend === null || trend === undefined) return { text: EMPTY_VALUE, tone: "muted", arrow: "" };
  const v = Number(trend);
  if (v > 0) return { text: `+${v}`, tone: "up", arrow: "↗" };
  if (v < 0) return { text: `−${Math.abs(v)}`, tone: "down", arrow: "↘" };
  return { text: "0", tone: "muted", arrow: "" };
};

// Badge copy for a change: "↗ +3.2K in 30d", "↘ −0.4pp". `fmt` formats the
// size of the change, `suffix` follows it. Null when there is no change on
// record (the badge is then hidden).
export const changeBadge = (delta, { fmt = (v) => `${v}`, suffix = "" } = {}) => {
  if (isMissing(delta)) return null;
  const { arrow } = trendLabel(Number(delta));
  return `${arrow ? `${arrow} ` : ""}${fmtSigned(delta, fmt)}${suffix}`;
};

// "50%" from a 0-1 ratio.
export const fmtRatioPct = (ratio) => (isMissing(ratio) ? EMPTY_VALUE : `${Math.round(Number(ratio) * 100)}%`);

// Roster CLASSIFICATION column: FR / SO / JR / SR / GR from whatever the
// feed stores ("Jr", "Junior", "11th", "SR"). Anything else shows as is.
const CLASS_WORDS = [
  ["FR", ["FR", "FRESHMAN", "9", "9TH"]],
  ["SO", ["SO", "SOPH", "SOPHOMORE", "10", "10TH"]],
  ["JR", ["JR", "JUNIOR", "11", "11TH"]],
  ["SR", ["SR", "SENIOR", "12", "12TH"]],
  ["GR", ["GR", "GRAD", "GRADUATE", "PG", "POSTGRAD"]],
];
export const classificationOf = (year) => {
  const word = String(year ?? "").trim().toUpperCase().replace(/[.\s-]/g, "");
  if (!word) return EMPTY_VALUE;
  const hit = CLASS_WORDS.find(([, words]) => words.includes(word));
  return hit ? hit[0] : String(year).trim();
};

// The college levels the server sends for a college athlete.
const COLLEGE_LEVELS = ["FR", "SO", "JR", "SR", "GR"];

// What a roster player's Classification shows: a college level ("JR",
// `collegeLevel`), else the graduation year ("2027", `gradYear`), else the
// class label above. The server sends `collegeLevel` for a college athlete
// and, on a college team, for every player from their class standing, so a
// college roster reads JR for a registered athlete and a pending player
// alike instead of a high school class year; high school and club rosters
// keep the grad year (and Rep Mode, which has no team, reads each athlete's
// own). A level such as "PSA" (Cerebro's prep circuit code) only shows when
// neither is on file. A college level the server only estimated from the
// high school class year (`collegeLevelEstimated`) says so: "FR (estimated)".
export const rosterClassOf = (p) => {
  const level = String(p?.collegeLevel ?? "").trim().toUpperCase();
  if (COLLEGE_LEVELS.includes(level)) return p?.collegeLevelEstimated ? `${level} (estimated)` : level;
  const year = Number.parseInt(p?.gradYear, 10);
  return Number.isFinite(year) && year > 0 ? String(year) : classificationOf(p?.year);
};

// Chart axis: the top tick and the ticks for `intervals` equal steps that
// cover `max`, with a round step (1, 2, 2.5 or 5 times a power of ten).
export const niceTicks = (max, intervals = 4) => {
  const raw = Math.max(Number(max) || 0, 1) / intervals;
  const power = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * power).find((s) => s >= raw);
  return Array.from({ length: intervals + 1 }, (_, i) => i * step);
};

export const playerSubtitle = (p) => [p?.position, p?.jersey ? `#${p.jersey}` : null].filter(Boolean).join(" · ");

export const initialsOf = (p) => `${(p?.name || " ")[0] || ""}${(p?.surname || " ")[0] || ""}`.trim().toUpperCase() || "?";

// Roster filter chips (same rules as the previous dashboard, with counts).
export const filterPlayers = (players, filter) => {
  const list = players || [];
  if (filter === "activated") return list.filter((p) => p.status === "active");
  if (filter === "pending") return list.filter((p) => p.status !== "active");
  if (filter === "standouts") {
    const s = list.filter((p) => (p.score || 0) >= 80);
    return s.length ? s : [...list].filter((p) => p.score != null).sort((a, b) => (b.score || 0) - (a.score || 0)).slice(0, 3);
  }
  return list;
};

export const rosterCounts = (players) => ({
  all: (players || []).length,
  activated: filterPlayers(players, "activated").length,
  pending: filterPlayers(players, "pending").length,
  standouts: filterPlayers(players, "standouts").length,
});

// The quadrant a scatter point falls in (sport on x, visibility on y).
export const archetypeOf = (perf, vis) => {
  const hiP = (perf || 0) >= 50;
  const hiV = (vis || 0) >= 50;
  if (hiP && hiV) return "complete";
  if (hiP) return "gem";
  if (hiV) return "star";
  return "dev";
};

// Swing Composition points: activated players with both a CacheValue and a
// NILx value (a missing NILx is not $0, so that player is left out rather
// than plotted as a buy signal), placed by CacheValue (x) and NILx (y) as a
// share of the roster's highest of each, and archetyped by the quadrant
// they sit in, so a dot's colour always matches the tinted area around it.
export const swingPoints = (players) => {
  const valued = (players || []).filter((p) => p.status === "active" && p.cacheValue != null && p.nilx != null);
  const maxX = Math.max(1, ...valued.map((p) => p.cacheValue));
  const maxY = Math.max(1, ...valued.map((p) => p.nilx));
  return valued.map((p) => {
    const x = Math.round((100 * p.cacheValue) / maxX);
    const y = Math.round((100 * p.nilx) / maxY);
    return { player: p, x, y, archetype: archetypeOf(x, y) };
  });
};

// athleteId -> swing archetype, for the tables and hover cards outside the
// chart.
export const swingArchetypes = (players) => Object.fromEntries(swingPoints(players).map((pt) => [pt.player.athleteId, pt.archetype]));

// Biggest swing (Team Performance): the Swing Composition point farthest
// from the diagonal. Below it the NIL market underprices the athlete (the
// buy signal); above it NILx outruns on-court value (a brand asset). Null
// when there is no point, or every point sits on the diagonal.
const swingOf = (pt) => Math.abs(pt.x - pt.y);
export const biggestSwing = (players) => {
  const best = swingPoints(players).reduce((top, pt) => (swingOf(pt) > (top ? swingOf(top) : 0) ? pt : top), null);
  if (!best) return null;
  return { ...best, signal: best.y < best.x ? "Buy signal" : "Brand asset" };
};

// Labels for the athlete hover card (Figma HoverCard 1552:7416). Anything
// the product has no data for is null, and the card hides that line.
export const hoverCardModel = (player, { teamName = "", archetype } = {}) => {
  const p = player || {};
  const active = p.status === "active";
  const range = p.cacheValueRange;
  return {
    name: `${p.name || ""} ${p.surname || ""}`.trim(),
    subtitle: [p.school || teamName, p.position, p.jersey ? `#${p.jersey}` : null].filter(Boolean).join(" · "),
    pending: !active,
    score: active ? (p.score ?? EMPTY_VALUE) : null,
    scoreSub: active && p.natlTopPct ? `Nat'l Top ${p.natlTopPct}%` : null,
    cacheValue: active ? fmtMoneyCompact(p.cacheValue) : null,
    valueSub: active && range && range.min != null && range.max != null ? `${fmtMoneyCompact(range.min)}–${fmtMoneyCompact(range.max)}` : null,
    archetype: active ? ARCHETYPE_META[archetype] || null : null,
  };
};

// A team's name as Coach Mode shows it, by the one display rule
// (utils/teamNames, D15): the server's displayName, else its name (a coach
// team's currentTeam, a dashboard team's name) with the age group its league
// key names ("Oakland Soldiers 16U"), never a season CACHE added. Identity
// stays on the team id and league; this is display only.
export const teamNameOf = (t) => {
  if (!t || typeof t !== "object") return "";
  const name = String(t.currentTeam || t.teamName || t.name || "").trim();
  return String(displayTeamName({ ...t, teamName: name, name }) || name).trim();
};

// An age group token ("16U", "U17") in a name or a label.
const AGE_TOKEN = /\b(?:\d{1,2}U|U\d{1,2})\b/gi;

// A league label as it shows beside a team: without the season CACHE adds
// (" · 2026"), a stored "competition|division" key read as words, and
// without an age group the team's name already says ("Oakland Soldiers 16U ·
// Nike EYBL (Boys)", never "16U" twice). A bare internal key ("cbx_...",
// "mbb") is no label: "".
export const leagueLabelOf = (label, title = "") => {
  let text = stripSeasonSuffix(String(label || ""))
    .replace(/\s*\|\s*/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (/^[a-z0-9_]+$/.test(text)) return "";
  const said = new Set((String(title).match(AGE_TOKEN) || []).map((age) => age.toUpperCase()));
  if (said.size) {
    text = text
      .replace(AGE_TOKEN, (age) => (said.has(age.toUpperCase()) ? "" : age))
      .replace(/\s+/g, " ")
      .replace(/\s*·\s*$/, "")
      .trim();
  }
  return text;
};

// Team switch: "Oakland Soldiers 15U · Nike EYBL (Boys)", the team's name
// and its league once each; the server's label for a team with no league.
export const teamOptionLabel = (t) => {
  if (!t) return "";
  const name = teamNameOf(t);
  const league = leagueLabelOf(t.cascade?.leagueDisplayName, name);
  return league && name ? `${name} · ${league}` : stripSeasonSuffix(String(t.label || "")).trim() || name || t.teamId || "";
};

export const teamKey = (t) => (t ? `${t.teamId}|${t.league || ""}` : "");

// The teams a coach can switch between in the current view (their own
// teams, or a league admin's granted teams).
export const teamsForView = (teams, view) => (teams || []).filter((t) => (view === "league" ? t.leagueAdmin : !t.leagueAdmin));

// The team a page's roster actions work on: the selected team, else the
// coach's first team, else none.
export const activeTeamOf = (selection) => selection.team || selection.teams[0] || null;

// Roster rows for RosterManagerModal from dashboard players.
export const rosterForManager = (players) =>
  (players || []).map((p) => ({
    id: p.playerId || (String(p.athleteId || "").startsWith("roster-") ? String(p.athleteId).slice(7) : ""),
    firstName: p.name,
    lastName: p.surname,
    position: p.position,
    jersey: p.jersey,
  }));

// Brand fit dot colours (Figma "Team Brand Category Alignment"): Strong
// green, Good blue, Moderate yellow, Low red, as the bm-positive, bm-info,
// bm-attention and bm-critical tokens.
export const fitTone = (fit) => {
  const f = (fit || "").toLowerCase();
  if (f.startsWith("strong")) return "#1f9d55";
  if (f.startsWith("good")) return "#2f6fed";
  if (f.startsWith("moderate")) return "#EDD200";
  if (f.startsWith("low")) return "#E2241A";
  return "#b8b8bc";
};

// Circle filter groups (Coach and Rep My Circle). Missing-guardian rows
// sit under Guardians.
export const circleFilter = (rows, filter) => {
  const list = rows || [];
  if (filter === "athletes") return list.filter((r) => r.kind === "athlete");
  if (filter === "guardians") return list.filter((r) => r.kind === "guardian" || r.kind === "missing");
  if (filter === "agents") return list.filter((r) => r.kind === "agent");
  return list;
};

// mailto draft for reaching an athlete or a contact from Coach Mode. `bcc`
// (a list of emails) is for group messages, so recipients stay private.
export const mailtoFor = ({ email = "", bcc = [], subject, body }) => {
  const params = [];
  if (bcc.length) params.push(`bcc=${bcc.map(encodeURIComponent).join(",")}`);
  if (subject) params.push(`subject=${encodeURIComponent(subject)}`);
  if (body) params.push(`body=${encodeURIComponent(body)}`);
  return `mailto:${email}${params.length ? `?${params.join("&")}` : ""}`;
};
